import { Camera, CameraConfig } from './types';

const CAMERAS_PATH = '/cameras';

function base(gatewayUrl: string): string {
  return `${gatewayUrl}${CAMERAS_PATH}`;
}

export async function getCameras(gatewayUrl: string): Promise<Camera[]> {
  const res = await fetch(`${base(gatewayUrl)}/api/config`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return Object.entries<CameraConfig>(data.cameras ?? {}).map(([name, config]) => ({
    name,
    config,
  }));
}

export function getStreamUrl(gatewayUrl: string, cameraName: string): string {
  const wsBase = gatewayUrl.replace(/^http/, 'ws');
  return `${wsBase}${CAMERAS_PATH}/live/mse/api/ws?src=${cameraName}`;
}

export function getThumbnailUrl(gatewayUrl: string, cameraName: string): string {
  return `${base(gatewayUrl)}/api/${cameraName}/latest.jpg`;
}

export async function updateCameraSetting(
  gatewayUrl: string,
  cameraName: string,
  key: string,
  value: boolean,
): Promise<void> {
  // Frigate 0.17 parses query-string params as config dot-keys; the body must be
  // present but empty. Do NOT pass save_config — it gets parsed as a config key and 400s.
  const res = await fetch(
    `${base(gatewayUrl)}/api/config/set?cameras.${cameraName}.${key}=${value}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export interface RecordingSegment {
  id: string;
  camera: string;
  start_time: number;
  end_time: number;
  duration: number;
  motion: number;
  objects: number;
}

export async function startManualEvent(
  gatewayUrl: string,
  cameraName: string,
): Promise<string> {
  const res = await fetch(
    `${base(gatewayUrl)}/api/events/${cameraName}/manual/create`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ include_recording: true, duration: null }),
    },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return data.event_id as string;
}

export async function endManualEvent(
  gatewayUrl: string,
  eventId: string,
): Promise<void> {
  const res = await fetch(
    `${base(gatewayUrl)}/api/events/${eventId}/end`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ end_time: Date.now() / 1000 }),
    },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

// A saved clip is a Frigate "event" (the manual REC button creates one). We list
// events (not raw continuous recordings) so the user sees discrete clips, and play
// them via the event clip.mp4 (H.264, plays directly in <video>).
export async function getRecordings(
  gatewayUrl: string,
  cameraName: string,
): Promise<RecordingSegment[]> {
  const res = await fetch(
    `${base(gatewayUrl)}/api/events?cameras=${cameraName}&limit=100`,
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const events = await res.json();
  return events
    .filter((e: any) => e.has_clip && e.end_time)
    .map((e: any) => ({
      id: e.id,
      camera: e.camera,
      start_time: e.start_time,
      end_time: e.end_time,
      duration: e.end_time - e.start_time,
      motion: 0,
      objects: 0,
    }));
}

// Event clip is a standalone H.264 MP4 — plays directly in a <video> element.
export function getClipUrl(gatewayUrl: string, eventId: string): string {
  return `${base(gatewayUrl)}/api/events/${eventId}/clip.mp4`;
}

// Removes the event from Frigate's list. Note: the underlying continuous footage is
// reclaimed by retention / the disk cap, not by this call.
export async function deleteRecording(gatewayUrl: string, eventId: string): Promise<void> {
  const res = await fetch(`${base(gatewayUrl)}/api/events/${eventId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export type PtzAction = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT' | 'STOP';

export async function ptzMove(
  gatewayUrl: string,
  cameraName: string,
  action: PtzAction,
): Promise<void> {
  const res = await fetch(`${base(gatewayUrl)}/api/ptz/${cameraName}/move`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'MOVE', argument: action }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}
